import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StampCard } from "@/components/StampCard";
import { ScanStampModal } from "@/components/admin/ScanStampModal";
import { Loader2, Save, Gift, Users, Award, Stamp, Search, Plus, UserPlus, Check, Phone, QrCode, KeyRound } from "lucide-react";
import { ResetPasswordDialog } from "@/components/admin/ResetPasswordDialog";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
    <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider">
      <Icon className="h-4 w-4" /> {label}
    </div>
    <p className="font-mono-j text-3xl font-bold mt-2">{value}</p>
  </div>
);

export const LoyaltyManager = ({ restaurant }) => {
  const accent = restaurant.primary_color || "#D9531E";
  const [program, setProgram] = useState(null);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [newMember, setNewMember] = useState({ phone: "", name: "" });
  const [adding, setAdding] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  const loadAll = async () => {
    const [p, s, m] = await Promise.all([
      api.get("/my/loyalty/program"),
      api.get("/my/loyalty/stats"),
      api.get("/my/loyalty/members"),
    ]);
    setProgram(p.data);
    setStats(s.data);
    setMembers(m.data);
    setLoading(false);
  };
  useEffect(() => {
    loadAll();
  }, []);

  const refresh = async () => {
    const [s, m] = await Promise.all([api.get("/my/loyalty/stats"), api.get("/my/loyalty/members")]);
    setStats(s.data);
    setMembers(m.data);
  };

  const saveProgram = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/my/loyalty/program", {
        program_name: program.program_name,
        stamps_required: parseInt(program.stamps_required) || 1,
        reward_description: program.reward_description,
      });
      setProgram(data);
      toast.success("Loyalty program saved");
    } catch {
      toast.error("Could not save program");
    } finally {
      setSaving(false);
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    if (!newMember.phone.trim()) return;
    setAdding(true);
    try {
      await api.post("/my/loyalty/members", newMember);
      setNewMember({ phone: "", name: "" });
      toast.success("Member added");
      refresh();
    } catch {
      toast.error("Could not add member");
    } finally {
      setAdding(false);
    }
  };

  const addStamp = async (id) => {
    try {
      const { data } = await api.post(`/my/loyalty/members/${id}/stamp`);
      toast.success(data.rewards_available > 0 ? "Stamp added — reward unlocked! 🎉" : "Stamp added");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const redeem = async (id) => {
    try {
      await api.post(`/my/loyalty/members/${id}/redeem`);
      toast.success("Reward redeemed");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [resetting, setResetting] = useState(null);

  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-orange-500" />;

  const filtered = members.filter((m) => m.phone.includes(query) || (m.name || "").toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold">Loyalty Program</h2>
          <p className="text-slate-400 text-sm">Reward regulars with a digital stamp card.</p>
        </div>
        <button data-testid="open-scan-button" onClick={() => setScanOpen(true)} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 transition-colors rounded-xl px-4 py-2.5 text-sm font-semibold shrink-0">
          <QrCode className="h-4 w-4" /> Scan to stamp
        </button>
      </div>

      <ScanStampModal open={scanOpen} onClose={() => setScanOpen(false)} onStamped={refresh} accent={accent} program={program} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Members" value={stats.total_members} />
        <StatCard icon={Stamp} label="Stamps issued" value={stats.total_stamps_issued} />
        <StatCard icon={Gift} label="Rewards pending" value={stats.rewards_pending} />
        <StatCard icon={Award} label="Rewards redeemed" value={stats.rewards_redeemed} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Program config */}
        <form onSubmit={saveProgram} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-sub font-semibold text-lg">Program settings</h3>
            <span data-testid="program-active-status" className={`text-xs font-semibold rounded-full px-3 py-1 ${program.active ? "bg-emerald-950/60 text-emerald-400" : "bg-slate-800 text-slate-400"}`} title="Turned on or off by the platform admin in Setup">
              {program.active ? "Active" : "Off — ask the platform admin"}
            </span>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Program name</label>
            <input data-testid="program-name-input" value={program.program_name} onChange={(e) => setProgram({ ...program, program_name: e.target.value })} className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500" />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Stamps required for reward</label>
            <input data-testid="program-stamps-input" type="number" min="1" max="20" value={program.stamps_required} onChange={(e) => setProgram({ ...program, stamps_required: e.target.value })} className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500" />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Reward description</label>
            <input data-testid="program-reward-input" value={program.reward_description} onChange={(e) => setProgram({ ...program, reward_description: e.target.value })} placeholder="e.g. A free coffee" className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500" />
          </div>
          <button data-testid="save-program-button" type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 rounded-xl px-6 py-3 font-semibold transition-colors">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save program
          </button>
        </form>

        {/* Live preview */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <h3 className="font-sub font-semibold text-lg mb-4">Customer preview</h3>
          <StampCard program={program} member={{ stamps: Math.min(3, program.stamps_required), rewards_available: 0 }} accent={accent} />
        </div>
      </div>

      {/* Members */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <h3 className="font-sub font-semibold text-lg">Members ({members.length})</h3>
          <form onSubmit={addMember} className="flex items-center gap-2 w-full sm:w-auto">
            <input data-testid="new-member-phone-input" value={newMember.phone} onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })} placeholder="Phone" className="bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm flex-1 min-w-0 sm:flex-none sm:w-32 focus:outline-none focus:border-orange-500" />
            <input data-testid="new-member-name-input" value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} placeholder="Name" className="bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm flex-1 min-w-0 sm:flex-none sm:w-28 focus:outline-none focus:border-orange-500" />
            <button data-testid="add-member-button" type="submit" disabled={adding} className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 rounded-xl px-3 py-2 text-sm font-semibold transition-colors shrink-0">
              <UserPlus className="h-4 w-4" /> Add
            </button>
          </form>
        </div>

        <div className="relative mb-4">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="member-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by phone or name..." className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-orange-500" />
        </div>

        {filtered.length === 0 ? (
          <div className="border border-dashed border-slate-700 rounded-xl p-8 text-center text-slate-400 text-sm">No members yet.</div>
        ) : (
          <div className="space-y-2">
            {filtered.map((m) => (
              <div key={m.id} data-testid={`member-row-${m.id}`} className="flex flex-wrap items-center justify-between gap-3 bg-slate-800/60 border border-slate-700/50 rounded-xl p-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-slate-700 grid place-items-center shrink-0">
                    <Phone className="h-4 w-4 text-slate-300" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-sub font-semibold text-sm truncate">{m.name || "Guest"}</p>
                    <p className="text-xs text-slate-400 font-mono-j">{m.phone}{m.has_account && <span data-testid={`member-has-account-${m.id}`} className="ml-2 font-sans text-[10px] uppercase tracking-wider text-emerald-400">Account</span>}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-300 font-mono-j bg-slate-900 rounded-full px-3 py-1.5">{m.stamps}/{program.stamps_required}</span>
                  {m.rewards_available > 0 && (
                    <span data-testid={`member-reward-${m.id}`} className="text-xs bg-amber-950/60 text-amber-400 rounded-full px-3 py-1.5 font-semibold">
                      {m.redeem_code}
                    </span>
                  )}
                  <button data-testid={`reset-password-${m.id}`} onClick={() => setResetting(m)} title="Reset login password" className="inline-flex items-center rounded-lg px-2.5 py-2 text-slate-300 bg-slate-700/70 hover:bg-slate-700 transition-colors">
                    <KeyRound className="h-4 w-4" />
                  </button>
                  <button data-testid={`add-stamp-${m.id}`} onClick={() => addStamp(m.id)} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-white transition-colors" style={{ backgroundColor: accent }}>
                    <Plus className="h-4 w-4" /> Stamp
                  </button>
                  {m.rewards_available > 0 && (
                    <button data-testid={`redeem-${m.id}`} onClick={() => redeem(m.id)} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 rounded-lg px-3 py-2 text-sm font-semibold transition-colors">
                      <Check className="h-4 w-4" /> Redeem
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {resetting && <ResetPasswordDialog member={resetting} onClose={() => setResetting(null)} onDone={() => { setResetting(null); refresh(); }} />}
    </div>
  );
};
